import * as React from 'react';
import { connect } from 'redux-scaffolding-ts';
import { DropDownPriorityStore, PriorityDto, PriorityStore } from '../../stores/configuration/events-n-requests/priorities-store';
import { nameof } from '../../utils/object';
import { ItemReference } from '../../stores/dataStore';
import { isNullOrWhiteSpaces } from '../../utils/useful-functions';
import TailwindSelect from 'widgets/editors/tailwind-select';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TailwindPriorityEditorProps {
  value: ItemReference;
  onChange?: (value: ItemReference) => void;
  className?: string;
  placeholder?: string;
  priorities?: PriorityStore;
  nullable?: boolean;
  readOnly?: boolean;
  clearable?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindPriorityEditor: React.FC<TailwindPriorityEditorProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
  priorities,
  nullable = false,
  readOnly = false,
  clearable = true,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<ItemReference>(value);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(true);

  const getPriorityMethod = useCallback(
    async (search: string = '') => {
      if (!isMounted.current) return;
      try {
        setIsLoading(true);
        const result = await priorities.getAllAsync(
          {
            searchQuery: '',
            skip: 0,
            take: 100000,
            orderBy: [{ direction: 'Ascending', field: nameof<PriorityDto>('name'), useProfile: false }],
            filter: isNullOrWhiteSpaces(search) ? [] : [`startswith(tolower(${nameof<PriorityDto>('name')}), '${search.toLowerCase()}')`]
          },
          search
        );

        if (isMounted.current && result) {
          setOptions(
            result.items.map(item => ({
              value: item.id,
              label: item.name
            }))
          );
        }
      } catch (error) {
        if (isMounted.current) {
          console.error('Error loading priorities:', error);
          setOptions([]);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    },
    [priorities]
  );

  useEffect(() => {
    isMounted.current = true;
    getPriorityMethod();

    return () => {
      isMounted.current = false;
    };
  }, [getPriorityMethod]);

  useEffect(() => {
    if (isMounted.current) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleChange = (newValue: string | null) => {
    if (!isMounted.current) return;
    const selected = newValue ? options.find(opt => opt.value === newValue) : null;
    const itemRef = selected ? { id: selected.value, title: selected.label } : null;
    setSelectedValue(itemRef);
    onChange?.(itemRef);
  };

  return (
    <TailwindSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(delivery => ({
              id: delivery.value,
              text: delivery.label
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={selectedValue?.id || ''}
      onChange={handleChange}
      placeholder={placeholder || 'Select an option'}
      clearable
      searchable={!isLoading}
      disabled={readOnly}
      className="w-full"
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['priorities', DropDownPriorityStore])(TailwindPriorityEditor);
